import { useState } from "react";
import { Bell, BellRing } from "lucide-react";
import DeskConfirm from "@/components/crm/DeskConfirm";
import UserAvatar from "@/components/ui/UserAvatar";
import api from "@/lib/api";
import { toast } from "@/lib/toast";
import { RELANCE_COLOR } from "@/components/tasks/RelanceBell";

/** iter410 L1-4 — case « Relancer » de la barre segmentée (icône 22 px #E0569B, libellé 11 px #C8C8CC, aucun fond). */
export function RelanceTile({ onClick, count = 0 }) {
  return (
    <button type="button" onClick={onClick} className="td-seg-cell" data-testid="td-relance" title={count ? `${count} relance(s) déjà envoyée(s)` : "Relancer les assignés"}>
      <span className="td-seg-icon relative" style={{ color: RELANCE_COLOR }}>
        <BellRing size={22} strokeWidth={2.3} />
        {count > 0 && <span className="absolute -top-1.5 -right-2 w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold tabular-nums" style={{ background: RELANCE_COLOR, color: "#1B1B1B" }} data-testid="td-relance-count">{count}</span>}
      </span>
      <span className="td-seg-label">Relancer</span>
    </button>
  );
}

const ROLE_LABEL = { admin: "Admin", manager: "Manager", user: "Utilisateur", chef_projet: "Chef de projet" };
const Pill = ({ bg, fg, children, testId }) => <span style={{ fontSize: 10.5, fontWeight: 600, padding: "3px 9px", borderRadius: 999, background: bg, color: fg }} data-testid={testId}>{children}</span>;

export const lateDays = (task) => {
  if (!task?.deadline || ["completed", "closed"].includes(task.status)) return 0;
  const d = Math.floor((Date.now() - new Date(task.deadline).getTime()) / 86400000);
  return d > 0 ? d : 0;
};

/** iter410 L2-7 (E3) — fenêtre R2 « Relancer TH… » : cloche 30 px, objet, pastilles n° / retard, carte destinataires, message 130 px, mention e-mail. */
export function RelanceDialog({ open, task, usersById = {}, onClose, onDone }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  if (!open || !task) return null;
  const n = (task.relances?.count || 0) + 1;
  const late = lateDays(task);
  const assignees = (task.assignments || []).map((a) => usersById[a.user_id] || { id: a.user_id, first_name: a.user_name || "?", last_name: "" });
  const names = assignees.map((u) => `${u.first_name || ""} ${u.last_name || ""}`.trim());
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.id}/relance`, { message: msg.trim() || null });
      toast.success("Tâche relancée");
      setMsg("");
      onDone?.(data);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Relance impossible"); } finally { setBusy(false); }
  };
  return (
    <DeskConfirm open danger={false} icon={Bell} iconBox={{ size: 30, bg: "#2E1A25", color: RELANCE_COLOR }} title={`Relancer ${task.reference || ""}`} subtitle={task.subject} confirmLabel="Relancer" cancelLabel="Annuler" busy={busy} onClose={() => { setMsg(""); onClose(); }} onConfirm={send} testId="relance-confirm">
      <div className="flex items-center justify-center" style={{ gap: 6 }} data-testid="relance-confirm-subtitle">
        <Pill bg="#2E1A25" fg="#F0A0C4" testId="relance-confirm-n">Relance n° {n}</Pill>
        {late > 0 && <Pill bg="#2E1818" fg="#F59A9A" testId="relance-confirm-late">Retard · {late} j</Pill>}
      </div>
      <div className="grid" style={{ marginTop: 10, gap: 6, background: "#121216", borderRadius: 10, padding: "8px 10px" }} data-testid="relance-confirm-recipients">
        {assignees.map((u) => (
          <div key={u.id} className="flex items-center" style={{ gap: 8 }} data-testid={`relance-confirm-recipient-${u.id}`}>
            <UserAvatar user={u} size={22} showPopover={false} />
            <span style={{ fontSize: 12, color: "#E8E8EC", fontWeight: 500 }}>{`${u.first_name || ""} ${u.last_name || ""}`.trim()}</span>
            <span style={{ fontSize: 10.5, color: "#8A8A93", marginLeft: "auto" }}>{ROLE_LABEL[u.role] || u.role || ""}</span>
          </div>
        ))}
      </div>
      <textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Message (optionnel)…" maxLength={600}
        style={{ marginTop: 10, width: "100%", height: 130, resize: "none", fontSize: 11.5, color: "#e8e8ec", background: "#121216", border: "1px solid #1F7AE0", borderRadius: 8, padding: "7px 9px", outline: "none", fontFamily: "inherit" }}
        data-testid="relance-confirm-message" />
      <div style={{ marginTop: 7, fontSize: 10.5, color: "#8A8A93", lineHeight: 1.4 }} data-testid="relance-confirm-mail-note">Un e-mail sera aussi envoyé à {names.join(", ")}.</div>
    </DeskConfirm>
  );
}
